import type { NavigationSplitViewTab } from "@repo/ui";
import type { FC } from "react";

import { dateTimeOf, name, snakeToPascal, splitWordsOnUpperCase } from "@repo/helpers";
import { Col, Loading, NavigationSplitView, Rating, Row } from "@repo/ui";
import { useMemo } from "react";

import type { SurveyRecord } from "../../../models/surveys";

import { useSurveyQuery } from "../../../api/repApi";
import { SurveyStatus } from "../../../models/surveys";
import style from "./style.module.scss";

export const Surveys: FC = () => {
	const { data, loaded } = useSurveyQuery();

	const tabs: NavigationSplitViewTab[] = data.map((s) => ({
		id: s.id,
		enrollmentId: s.enrollmentId,
		tab: <SurveyTab {...s} />,
		detail: <SurveyDetails {...s} />,
	}));

	return (
		<Loading loading={!loaded}>
			<SurveyWidget records={data} />
			{tabs.length > 0 ? (
				<NavigationSplitView id="surveys" tabs={tabs} className={style.navigationSplitView} scrollable />
			) : null}
		</Loading>
	);
};

const SurveyTab: FC<SurveyRecord> = ({ enrollmentId, createdOn, dispositionDate }) => {
	return (
		<div className={style.surveyTabRoot}>
			<h6 className={style.tabHeader}>Enrollment Id:</h6>
			<p className={style.tabDetailBody}>{enrollmentId}</p>
			<p className={style.tabDetailTitle}>Created On:</p>
			<p className={style.tabDetailBody}>{dateTimeOf(createdOn)}</p>
			<p className={style.tabDetailTitle}>Disposition Date:</p>
			<p className={style.tabDetailBody}>{dateTimeOf(dispositionDate)}</p>
		</div>
	);
};

const SurveyDetails: FC<SurveyRecord> = (data) => {
	return (
		<div className={style.surveyDetails}>
			<dl>
				<dt>Status</dt>
				<dd>{SurveyStatus[data.status]}</dd>
				<dt>Customer</dt>
				<dd>{name(data.customerFirstName, data.customerLastName)}</dd>
				{data.rating ? (
					<>
						<dt>Rating</dt>
						<dd>
							<Rating value={parseInt(data.rating)} precision={0.1} readOnly />
						</dd>
					</>
				) : null}
			</dl>
			<div className={style.card}>
				<table className={style.table}>
					<thead>
						<tr>
							<td>
								<strong>Question</strong>
							</td>
							<td>
								<strong>Response</strong>
							</td>
						</tr>
					</thead>
					<tbody>
						{data.responses.map((r) => (
							<tr key={r.surveyResponseId}>
								<td>{splitWordsOnUpperCase(snakeToPascal(r.questionTag))}</td>
								<td>{splitWordsOnUpperCase(snakeToPascal(r.value))}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
};

interface Props {
	records: SurveyRecord[];
}

const SurveyWidget: FC<Props> = ({ records }) => {
	const data = useMemo(() => {
		const responses = records.map((record) => [...record.responses]).flat();

		const good = responses.filter((response) => response.value === "yes").length;
		const total = responses.length;
		const ratings = records.filter((record) => record.rating).map((record) => record.rating);
		const avgRating = ratings.reduce((a, b) => a + parseInt(b), 0) / ratings.length;

		return {
			good,
			total,
			avgRating,
		};
	}, [records]);

	return (
		<div className={style.surveyWidgetCard}>
			<Row>
				<Col>
					<h4>Good Responses</h4>
					<h6>{data?.good}</h6>
				</Col>
				<Col>
					<h4>Total Responses</h4>
					<h6>{data?.total}</h6>
				</Col>
				<Col>
					<h4>Average Rating</h4>
					<Rating value={data?.avgRating} precision={0.1} readOnly />
				</Col>
			</Row>
		</div>
	);
};
